// src/components/features/analysis/DynamicAnalysisTab.jsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useDynamicAnalysis } from '../../../hooks/useDynamicAnalysis';
import LoadingSpinner from '../../common/LoadingSpinner';
import ErrorMessage from '../../common/ErrorMessage';

const DynamicContainer = styled.div`
  padding: 2rem;
`;

const StatusSection = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatusTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: #1a1a1a;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'completed':
        return '#dcfce7';
      case 'pending':
        return '#dbeafe';
      case 'not_submitted':
        return '#f3f4f6';
      case 'unavailable':
        return '#fee2e2';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed':
        return '#166534';
      case 'pending':
        return '#1e40af';
      case 'not_submitted':
        return '#4b5563';
      case 'unavailable':
        return '#991b1b';
      default:
        return '#4b5563';
    }
  }};
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.primary && `
    background: #0066cc;
    color: white;
    &:hover:not(:disabled) {
      background: #0052a3;
    }
  `}
  
  ${props => props.secondary && `
    background: #6c757d;
    color: white;
    &:hover:not(:disabled) {
      background: #5a6268;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResultsSection = styled.div`
  margin-top: 2rem;
`;

const ResultsTitle = styled.h3`
  font-size: 1.25rem;
  color: #1a1a1a;
  margin-bottom: 1rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.875rem;
    color: #6b7280;
    text-transform: uppercase;
  }
  
  p {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #0066cc;
  }
`;

const DetailSection = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  h4 {
    margin: 0 0 1rem 0;
    font-size: 1rem;
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const DetailList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    padding: 0.5rem;
    border-bottom: 1px solid #f3f4f6;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 0.875rem;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #9ca3af;
  
  h3 {
    margin-top: 1rem;
    color: #6b7280;
  }
  
  p {
    margin: 0.5rem 0 0 0;
    font-size: 0.9rem;
  }
`;

const MessageBox = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 6px;
  background: ${props => props.success ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.success ? '#166534' : '#991b1b'};
  border: 1px solid ${props => props.success ? '#86efac' : '#fca5a5'};
`;

const DynamicAnalysisTab = ({ analysisData, sha256 }) => {
  const {
    dynamicStatus,
    loading,
    error,
    canSubmit,
    submitting,
    fetching,
    rescanning,
    submitForAnalysis,
    fetchResults,
    rescanAnalysis,
    refetch
  } = useDynamicAnalysis(sha256);

  const [message, setMessage] = useState(null);

  // Extract values from dynamicStatus (before early returns for hooks rules)
  const status = dynamicStatus?.status || 'not_submitted';
  const externalStatus = dynamicStatus?.external_status;
  const results = dynamicStatus?.results || {};
  const hasReport = !!results.report;
  const submittedAt = dynamicStatus?.submitted_at;
  const completedAt = results.completed_at;
  const isEnabled = dynamicStatus?.enabled !== false;

  // Debug logging (must be before any early returns)
  React.useEffect(() => {
    console.log('[DynamicAnalysisTab] Debug Info:', {
      status,
      externalStatus,
      hasReport,
      resultsKeys: Object.keys(results),
      reportExists: !!results.report
    });
  }, [status, externalStatus, hasReport, results]);

  // Determine if we should show results
  const shouldShowResults = hasReport && (status === 'completed' || status === 'pending');

  // Handler functions
  const handleSubmit = async () => {
    const result = await submitForAnalysis();
    setMessage(result);
    setTimeout(() => setMessage(null), 5000);
  };

  const handleFetch = async () => {
    const result = await fetchResults();
    if (result.message) {
      setMessage({ success: result.success, message: result.message });
    } else {
      setMessage(result);
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const handleRescan = async () => {
    const result = await rescanAnalysis();
    if (result.success) {
      setMessage({ 
        success: true, 
        message: 'Rescan initiated successfully. Previous results are shown below and will be updated when the new analysis completes.' 
      });
    } else {
      setMessage(result);
    }
    setTimeout(() => setMessage(null), 7000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Early returns after all hooks
  if (loading) {
    return <LoadingSpinner>Loading dynamic analysis status...</LoadingSpinner>;
  }

  if (!isEnabled) {
    return (
      <DynamicContainer>
        <EmptyState>
          <div style={{ fontSize: '3rem' }}>🔒</div>
          <h3>Dynamic Analysis Disabled</h3>
          <p>Dynamic analysis service is not enabled on this platform.</p>
        </EmptyState>
      </DynamicContainer>
    );
  }

  return (
    <DynamicContainer>
      <StatusSection>
        <StatusHeader>
          <StatusTitle>Dynamic Analysis Status</StatusTitle>
          <StatusBadge status={status}>
            {status === 'not_submitted' && 'Not Submitted'}
            {status === 'pending' && (
              externalStatus === 'analyzing' ? '⏳ Analyzing' : 
              externalStatus === 'scheduled' ? '📋 Scheduled' : 
              'Analysis Pending'
            )}
            {status === 'completed' && '✅ Completed'}
            {status === 'unavailable' && 'Service Unavailable'}
          </StatusBadge>
        </StatusHeader>

        {message && (
          <MessageBox success={message.success}>
            {message.success ? message.message : message.error}
          </MessageBox>
        )}

        <ButtonsContainer>
          {status === 'not_submitted' && (
            <ActionButton
              primary
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Submitting...' : '📤 Submit for Analysis'}
            </ActionButton>
          )}

          {status === 'pending' && (
            <ActionButton
              primary
              disabled={fetching}
              onClick={handleFetch}
            >
              {fetching ? 'Fetching...' : '🔄 Fetch Results'}
            </ActionButton>
          )}

          {status === 'completed' && (
            <>
              <ActionButton
                primary
                disabled={fetching}
                onClick={handleFetch}
              >
                {fetching ? 'Refreshing...' : '🔄 Refresh Results'}
              </ActionButton>
              <ActionButton
                secondary
                disabled={rescanning}
                onClick={handleRescan}
              >
                {rescanning ? 'Rescanning...' : '🔁 Rescan'}
              </ActionButton>
            </>
          )}
        </ButtonsContainer>

        {dynamicStatus?.last_checked && (
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
            Last checked: {formatDate(dynamicStatus.last_checked)}
          </p>
        )}
        
        {status === 'pending' && !hasReport && (externalStatus === 'analyzing' || externalStatus === 'scheduled') && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#dbeafe', borderRadius: '6px' }}>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#1e40af' }}>
              <strong>Status:</strong> {externalStatus === 'scheduled' ? 'Queued for analysis' : 'Currently analyzing'}
            </p>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#1e40af' }}>
              <strong>Analysis ID:</strong> {dynamicStatus.analysis_id || sha256}
            </p>
            {dynamicStatus.last_checked && (
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                <strong>External Service Last Update:</strong> {formatDate(dynamicStatus.last_checked)}
              </p>
            )}
          </div>
        )}
      </StatusSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {status === 'pending' && hasReport && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1.5rem',
          background: '#fff3cd', 
          border: '1px solid #ffc107',
          borderRadius: '6px',
          color: '#856404'
        }}>
          <strong>⚠️ Note:</strong> {
            externalStatus === 'scheduled' 
              ? `A new analysis is scheduled. The results below are from ${completedAt ? formatDate(completedAt) : 'a previous analysis'}.`
              : externalStatus === 'analyzing'
              ? `A new analysis is in progress. The results below are from ${completedAt ? formatDate(completedAt) : 'a previous analysis'} and will be updated when complete.`
              : `Analysis is pending. The results below are from ${completedAt ? formatDate(completedAt) : 'a previous analysis'}.`
          }
        </div>
      )}

      {shouldShowResults && (
        <ResultsSection>
          <ResultsTitle>Analysis Results</ResultsTitle>

          <InfoGrid>
            <InfoCard>
              <h4>Analysis Date</h4>
              <p style={{ fontSize: '1rem' }}>{completedAt ? formatDate(completedAt) : formatDate(results.report?.isodate) || 'N/A'}</p>
            </InfoCard>
            <InfoCard>
              <h4>Analysis Status</h4>
              <p style={{ fontSize: '1rem' }}>{results.report?.status || externalStatus || 'N/A'}</p>
            </InfoCard>
            <InfoCard>
              <h4>Crashed</h4>
              <p style={{ fontSize: '1rem' }}>{results.report?.analysis?.crashed ? 'Yes' : 'No'}</p>
            </InfoCard>
            <InfoCard>
              <h4>Timeout (seconds)</h4>
              <p>{results.report?.analysis?.settings?.timeout || 'N/A'}</p>
            </InfoCard>
          </InfoGrid>

          {results.report?.network?.insights && (
            <DetailSection>
              <h4>🌐 Network Activity</h4>
              {results.report.network.insights.ip?.length > 0 && (
                <>
                  <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    IP Addresses ({results.report.network.insights.ip.length})
                  </h5>
                  <DetailList>
                    {results.report.network.insights.ip.map((ip, index) => (
                      <li key={index}>{ip}</li>
                    ))}
                  </DetailList>
                </>
              )}
              {results.report.network.insights.dns?.length > 0 && (
                <>
                  <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    DNS Queries ({results.report.network.insights.dns.length})
                  </h5>
                  <DetailList>
                    {results.report.network.insights.dns.map((dns, index) => (
                      <li key={index}>{dns}</li>
                    ))}
                  </DetailList>
                </>
              )}
            </DetailSection>
          )}

          {results.report?.filesystem?.insights && (
            <DetailSection>
              <h4>📁 Filesystem Activity</h4>
              {results.report.filesystem.insights.interesting_files?.length > 0 && (
                <>
                  <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Interesting Files ({results.report.filesystem.insights.interesting_files.length})
                  </h5>
                  <DetailList>
                    {results.report.filesystem.insights.interesting_files.map((file, index) => (
                      <li key={index}>{file}</li>
                    ))}
                  </DetailList>
                </>
              )}
            </DetailSection>
          )}

          {results.report?.processes?.insights && (
            <DetailSection>
              <h4>⚙️ Process Activity</h4>
              {results.report.processes.details?.length > 0 && (
                <>
                  <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Running Processes ({results.report.processes.details.length})
                  </h5>
                  <DetailList>
                    {results.report.processes.details.map((proc, index) => (
                      <li key={index}>
                        {proc.name} (PID: {proc.pid}, User: {proc.user})
                      </li>
                    ))}
                  </DetailList>
                </>
              )}
            </DetailSection>
          )}

          {results.analysis?.artifacts?.length > 0 && (
            <DetailSection>
              <h4>📦 Available Artifacts</h4>
              <DetailList>
                {results.analysis.artifacts.map((artifact, index) => (
                  <li key={index}>📄 {artifact}</li>
                ))}
              </DetailList>
            </DetailSection>
          )}
        </ResultsSection>
      )}

      {status === 'not_submitted' && (
        <EmptyState>
          <div style={{ fontSize: '3rem' }}>📊</div>
          <h3>No Dynamic Analysis</h3>
          <p>This APK has not been submitted for dynamic analysis yet.</p>
          {canSubmit && <p>Click the button above to submit it for analysis.</p>}
        </EmptyState>
      )}

      {status === 'pending' && !hasReport && (
        <>
          {externalStatus === 'scheduled' ? (
            <EmptyState>
              <div style={{ fontSize: '3rem' }}>📋</div>
              <h3>Analysis Scheduled</h3>
              <p>The dynamic analysis has been queued and will start shortly.</p>
              {dynamicStatus.last_checked && (
                <p style={{ marginTop: '1rem' }}>
                  <strong>Last Update:</strong> {formatDate(dynamicStatus.last_checked)}
                </p>
              )}
              <p>Click "Fetch Results" to check the current status.</p>
            </EmptyState>
          ) : externalStatus === 'analyzing' && dynamicStatus.last_checked ? (
            <EmptyState>
              <div style={{ fontSize: '3rem' }}>⏳</div>
              <h3>Analysis in Progress</h3>
              <p>The dynamic analysis is currently running.</p>
              <p style={{ marginTop: '1rem' }}>
                <strong>Last Update:</strong> {formatDate(dynamicStatus.last_checked)}
              </p>
              <p>Click "Fetch Results" to check if it's completed.</p>
            </EmptyState>
          ) : (
            <EmptyState>
              <div style={{ fontSize: '3rem' }}>⏳</div>
              <h3>Analysis in Progress</h3>
              <p>The dynamic analysis is currently running.</p>
              <p>Click "Fetch Results" to check if it's completed.</p>
            </EmptyState>
          )}
        </>
      )}
    </DynamicContainer>
  );
};

export default DynamicAnalysisTab;